/**
 * The five first actions, and nothing else.
 *
 * Split from StartHere.jsx so the tests can read the list without standing
 * up a Supabase client: the component imports userData, which creates one at
 * module scope. Same split as setsArchive.js and concepts/index.js, for the
 * same reason.
 */

/* The five. Ordered, and the order matters: the tour first because it makes
   the other four legible, then the surface built to absorb the overwhelm,
   then three things that only mean something once you have done them. */
export const STEPS = [
  { id: "tour",  title: "Take the two minute tour",
    blurb: "What's on the platform and where, once, so the sidebar stops being a guess.",
    cta: "Start the tour", kind: "tour" },
  { id: "lab",   title: "Open the Learning Lab",
    blurb: "The answer to there being too much. It picks for you: a warmup, one thing, done.",
    cta: "Open it", route: "learn" },
  { id: "save",  title: "Save a drill you'd actually run",
    blurb: "Anything you save lands in your Portal, so the library becomes yours rather than ours.",
    cta: "Find one", route: "pd" },
  { id: "intro", title: "Say hello in the Community",
    blurb: "Tell us who you coach. It is the fastest way for the rest of this to get useful to you.",
    cta: "Introduce yourself", route: "community", kind: "intro" },
  { id: "calls", title: "Put the next call in your calendar",
    blurb: "Live group calls every week. Add them once and you never have to remember again.",
    cta: "See the calls", route: "calls" },
];

/* The intro step (Coleman, 2026-10-15) opens the Community composer with
   these lines filled in and tags the post "Intro", and it only counts once
   they actually post: visiting the Community isn't saying hello. */
export const INTRO_TAG = "Intro";
export const INTRO_STARTER = "Who I coach: \nWhere: ";

/* Does arriving at this route complete a step? Not for the intro. */
export const stepForArrival = (route) => STEPS.find(s => s.route && s.route === route && s.kind !== "intro");

/**
 * What is left of the list, and whether it is finished.
 *
 * Lifted out of StartHere.jsx because the dashboard needs the same answer:
 * Start Here is no longer a card of its own, it is a candidate for the one
 * notice slot (constants/nextAction.js), so BAMFull has to know which step
 * is next without rendering the component. Two copies of "is this step
 * done" would have drifted the first time a signal changed.
 *
 * `state` is the stored map, {stepId: isoDate}, plus `__dismissed`.
 *
 * A step counts as done if it was stored OR if the world says so: saved
 * drills mean the save step happened whatever the map says, and the tour
 * flag lives in localStorage because the tour predates this list. Arriving
 * at a page is what marks the routed steps, and the component still does
 * that marking; this only reads.
 */
export function startHereRemaining(state, { favCount = 0, tourDone = false } = {}) {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return { done: false, remaining: [], loaded: false };
  }
  if (state.__dismissed) return { done: true, remaining: [], loaded: true };

  const realSignal = (id) =>
    (id === "save" && (favCount || 0) > 0) ||
    (id === "tour" && !!tourDone);

  const remaining = STEPS
    .filter(s => !state[s.id] && !realSignal(s.id))
    .map(s => ({ id: s.id, title: s.title, cta: s.cta, to: s.kind === "intro" ? "intro" : (s.route || "tour") }));

  return { done: remaining.length === 0, remaining, loaded: true };
}

/**
 * Is this coach new enough for the list to be worth showing at all?
 *
 * The component's rule, kept: somebody who has already done most of it on
 * their own never needed a start page. Deciding this per render would make
 * the card appear and vanish as they work, so it is answered once.
 */
export function startHereWorthShowing(state, opts = {}) {
  const { remaining, loaded } = startHereRemaining(state, opts);
  if (!loaded || state.__dismissed) return false;
  const done = STEPS.length - remaining.length;
  return done < STEPS.length && done <= 3;
}
